import { findCountryCode } from "@redotech/locale/countries";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import { returnsCenterIdFormat } from "@redotech/redo-model/ids";
import {
  ConsolidationShipToAddress,
  DEFAULT_RETURNS_PROCESSING_COST,
  ExternalLocationHubConfig,
  ReturnProcessingLevel,
  ReturnsCenterConfig,
  Team,
} from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  Input,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import { deepIntersectionEquals } from "@redotech/util/equal";
import { memo, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMerchantAppLogin } from "../../../merchant-app";
import {
  AvailableReturnsCentersTable,
  ReturnsCenterConfigFormValue,
} from "./available-returns-centers-table";
import { SettingCard } from "./component/setting-card";

function formatCents(value: number): string {
  return value.toFixed(2);
}

function formatCentsOptional(value: number | undefined | null): string {
  if (value == null || value === 0) return "";
  return value.toFixed(2);
}

export const availableReturnsCentersInput = input<
  ReturnsCenterConfigFormValue[]
>({ equal: deepIntersectionEquals });

export const redoManagedReturnsSettingsForm = groupInput({
  enabled: input<boolean>(),
  processingCost: input<string>({ validator: numberValidator({ min: 0 }) }),
  processingCoverageShare: input<string>({
    validator: numberValidator({ min: 0 }),
  }),
  consolidationCoverageShare: input<string>({
    validator: numberValidator({ min: 0 }),
  }),
  returnProcessingLevel: input<ReturnProcessingLevel>(),
  externalLocationHubMappings: input<
    Record<string, { provider: string; locationId: string }>
  >({ equal: deepIntersectionEquals }),
  availableReturnsCenters: availableReturnsCentersInput,
});

export type RedoManagedReturnsSettingsForm = InputProvider.Form<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputProvider.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsSettingsDefault: RedoManagedReturnsSettingsValue =
  {
    enabled: false,
    processingCost: `${DEFAULT_RETURNS_PROCESSING_COST}`,
    processingCoverageShare: "",
    consolidationCoverageShare: "",
    returnProcessingLevel: ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings: {},
    availableReturnsCenters: [],
  };

export const redoManagedReturnsTeamToFormValue = (
  team: Team,
): RedoManagedReturnsSettingsValue => {
  const storedMappings =
    team.settings.redoManagedReturns?.externalLocationHubMappings;
  const formMappings: Record<string, { provider: string; locationId: string }> =
    {};
  if (storedMappings) {
    for (const [key, value] of Object.entries(storedMappings)) {
      // DB stores raw numeric keys; convert to formatted for form display
      const formattedKey =
        key && !returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.write(BigInt(key))
          : key;
      formMappings[formattedKey] = {
        provider: value.provider,
        locationId: value.locationId ?? "",
      };
    }
  }

  const storedCenters =
    team.settings.redoManagedReturns?.availableReturnsCenters ?? [];
  const formCenters: ReturnsCenterConfigFormValue[] = storedCenters.map(
    (config) => {
      const formattedCenterId =
        config.returnsCenterId &&
        !returnsCenterIdFormat.test(config.returnsCenterId)
          ? returnsCenterIdFormat.write(BigInt(config.returnsCenterId))
          : config.returnsCenterId;
      return {
        returnsCenterId: formattedCenterId,
        consolidationScheduleDays:
          config.consolidationScheduleDays?.toString() ?? "",
        shipToName: config.shipToAddress?.name ?? "",
        shipToStreet1: config.shipToAddress?.street1 ?? "",
        shipToStreet2: config.shipToAddress?.street2 ?? "",
        shipToCity: config.shipToAddress?.city ?? "",
        shipToState: config.shipToAddress?.state ?? "",
        shipToZip: config.shipToAddress?.zip ?? "",
        shipToCountry:
          findCountryCode(config.shipToAddress?.country ?? "") ??
          config.shipToAddress?.country ??
          "",
        refulfillment: config.refulfillment ?? false,
        processingFee: formatCentsOptional(config.processingFee),
      };
    },
  );

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingCost: formatCents(
      team.settings.redoManagedReturns?.processingCost ??
        DEFAULT_RETURNS_PROCESSING_COST,
    ),
    processingCoverageShare: formatCentsOptional(
      team.settings.redoManagedReturns?.processingCoverageShare,
    ),
    consolidationCoverageShare: formatCentsOptional(
      team.settings.redoManagedReturns?.consolidationCoverageShare,
    ),
    returnProcessingLevel:
      team.settings.redoManagedReturns?.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings: formMappings,
    availableReturnsCenters: formCenters,
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
): Team["settings"]["redoManagedReturns"] => {
  const mappings = redoManagedReturnsSettings.externalLocationHubMappings;
  const hubMappings: Record<string, ExternalLocationHubConfig> = {};
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      if (value.provider) {
        // Convert formatted key (rctr_...) back to raw numeric for DB storage
        const rawKey = returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.read(key).toString()
          : key;
        hubMappings[rawKey] = {
          provider: value.provider as ExternalLocationHubConfig["provider"],
          locationId:
            value.locationId && value.locationId.trim().length > 0
              ? value.locationId
              : undefined,
        };
      }
    }
  }

  const formCenters = redoManagedReturnsSettings.availableReturnsCenters ?? [];
  const availableReturnsCenters: ReturnsCenterConfig[] = formCenters.map(
    (c) => {
      const rawCenterId = returnsCenterIdFormat.test(c.returnsCenterId)
        ? returnsCenterIdFormat.read(c.returnsCenterId).toString()
        : c.returnsCenterId;

      const hasAddress = c.shipToName || c.shipToStreet1;
      const shipToAddress: ConsolidationShipToAddress | undefined = hasAddress
        ? {
            name: c.shipToName,
            street1: c.shipToStreet1,
            street2: c.shipToStreet2 || undefined,
            city: c.shipToCity,
            state: c.shipToState || undefined,
            zip: c.shipToZip,
            country: c.shipToCountry,
          }
        : undefined;

      const days = Number(c.consolidationScheduleDays);
      const processingFee = Number(c.processingFee);

      return {
        returnsCenterId: rawCenterId,
        consolidationScheduleDays:
          c.consolidationScheduleDays === "" || isNaN(days) ? undefined : days,
        shipToAddress,
        refulfillment: c.refulfillment ?? undefined,
        processingFee:
          c.processingFee === "" || isNaN(processingFee)
            ? undefined
            : processingFee,
      };
    },
  );

  const processingCost = isNaN(
    Number(redoManagedReturnsSettings.processingCost),
  )
    ? DEFAULT_RETURNS_PROCESSING_COST
    : Number(redoManagedReturnsSettings.processingCost);
  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: processingCost,
    processingCost,
    processingCoverageShare:
      Number(redoManagedReturnsSettings.processingCoverageShare) || 0,
    consolidationCoverageShare:
      Number(redoManagedReturnsSettings.consolidationCoverageShare) || 0,
    returnProcessingLevel:
      redoManagedReturnsSettings.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings:
      Object.keys(hubMappings).length > 0 ? hubMappings : undefined,
    availableReturnsCenters:
      availableReturnsCenters.length > 0 ? availableReturnsCenters : undefined,
  };
};

const processingLevelOptions: RedoListItem<ReturnProcessingLevel>[] =
  Object.values(ReturnProcessingLevel).map((returnProcessingLevel) => ({
    type: "text" as const,
    id: returnProcessingLevel,
    value: returnProcessingLevel,
    text:
      returnProcessingLevel.charAt(0).toUpperCase() +
      returnProcessingLevel.slice(1),
  }));

export const RedoManagedReturnsSettingsCardV2 = memo(
  function RedoManagedReturnsSettingsCardV2({
    input,
    teamId,
    coveragePriceLimit,
  }: {
    input: RedoManagedReturnsSettingsForm;
    teamId: string;
    coveragePriceLimit: number;
  }) {
    const totalCoverageShare =
      (Number(input.inputs.processingCoverageShare.value) || 0) +
      (Number(input.inputs.consolidationCoverageShare.value) || 0);
    const exceedsCoveragePrice = totalCoverageShare > coveragePriceLimit;

    const openMerchantApp = useMerchantAppLogin();

    const handleOpenLocationFlow = useCallback(() => {
      openMerchantApp(teamId, "/settings/shipping/location-flow-editor");
    }, [openMerchantApp, teamId]);

    const selectedProcessingLevel = useMemo(() => {
      return processingLevelOptions.find(
        (option) => option.value === input.inputs.returnProcessingLevel.value,
      );
    }, [input.inputs.returnProcessingLevel.value]);

    const handleProcessingLevelSelected = useCallback(
      ({ item }: { item: RedoListItem<ReturnProcessingLevel> }) => {
        input.inputs.returnProcessingLevel.setValue(item.value);
      },
      [input.inputs.returnProcessingLevel],
    );

    const disabled = !input.inputs.enabled.value;

    return (
      <>
        <BillingCard
          consolidationCoverageShare={input.inputs.consolidationCoverageShare}
          coveragePriceLimit={coveragePriceLimit}
          disabled={disabled}
          exceedsCoveragePrice={exceedsCoveragePrice}
          processingCost={input.inputs.processingCost}
          processingCoverageShare={input.inputs.processingCoverageShare}
          totalCoverageShare={totalCoverageShare}
        />
        <SettingCard>
          <Flex dir="column" gap="lg" p="xl">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Operations
            </Text>
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              label="Processing Level"
              options={processingLevelOptions}
              optionSelected={handleProcessingLevelSelected}
              placeholder="Select processing level..."
              selectedItem={selectedProcessingLevel}
              size="xs"
            />
          </Flex>
        </SettingCard>
        <SettingCard>
          <Flex dir="column" gap="lg">
            <Flex align="center" justify="space-between" pt="xl" px="xl">
              <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                Returns Centers
              </Text>
              <Flex
                align="center"
                gap="xxs"
                onClick={handleOpenLocationFlow}
                style={{ cursor: "pointer" }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  textColor="brand-primary"
                >
                  Configure location flow
                </Text>
                <LinkExternal02Icon
                  style={{
                    width: 14,
                    height: 14,
                    color: "var(--redo-colors-brand-500)",
                  }}
                />
              </Flex>
            </Flex>
            <AvailableReturnsCentersTable
              disabled={disabled}
              hubMappings={input.inputs.externalLocationHubMappings.value}
              input={input.inputs.availableReturnsCenters}
              onHubMappingsChange={
                input.inputs.externalLocationHubMappings.setValue
              }
              teamId={teamId}
            />
          </Flex>
        </SettingCard>
      </>
    );
  },
);

const CHEVRON_BASE: React.CSSProperties = {
  width: 20,
  height: 20,
  transition: "transform 0.2s ease",
  color: "var(--redo-colors-text-text-tertiary-600)",
  flexShrink: 0,
};

const CHEVRON_OPEN: React.CSSProperties = {
  ...CHEVRON_BASE,
  transform: "rotate(90deg)",
};

const CHEVRON_CLOSED: React.CSSProperties = {
  ...CHEVRON_BASE,
  transform: "rotate(0deg)",
};

const CLICKABLE: React.CSSProperties = {
  cursor: "pointer",
  userSelect: "none",
};

const LINK_ICON_STYLE: React.CSSProperties = { width: 14, height: 14 };

const CoverageShareLabel = memo(function CoverageShareLabel() {
  return (
    <Flex align="center" gap="xs">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        Paid for by coverage
      </Text>
      <Link to="../returns/return-protection">
        <LinkExternal02Icon style={LINK_ICON_STYLE} />
      </Link>
    </Flex>
  );
});

const BillingCard = memo(function BillingCard({
  processingCost,
  processingCoverageShare,
  consolidationCoverageShare,
  disabled,
  exceedsCoveragePrice,
  totalCoverageShare,
  coveragePriceLimit,
}: {
  processingCost: Input<string>;
  processingCoverageShare: Input<string>;
  consolidationCoverageShare: Input<string>;
  disabled: boolean;
  exceedsCoveragePrice: boolean;
  totalCoverageShare: number;
  coveragePriceLimit: number;
}) {
  const [processingOpen, setProcessingOpen] = useState(true);
  const [consolidationOpen, setConsolidationOpen] = useState(true);

  return (
    <SettingCard>
      <div>
        <Flex dir="column" gap="lg" p="xl">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Billing
          </Text>
          {exceedsCoveragePrice && (
            <Text fontSize="xs" textColor="error">
              {`Total coverage shares ($${totalCoverageShare.toFixed(2)}) exceed limit ($${coveragePriceLimit.toFixed(2)})`}
            </Text>
          )}
        </Flex>
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setProcessingOpen((v) => !v)}
          p="xl"
          style={CLICKABLE}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Processing
          </Text>
          <ChevronRightIcon
            style={processingOpen ? CHEVRON_OPEN : CHEVRON_CLOSED}
          />
        </Flex>
        {processingOpen && (
          <Flex align="flex-end" gap="md" pb="xl" pl="xl" pr="xl">
            <Flex grow="1">
              <RedoTextInput
                IconTrailing={() => <span>USD</span>}
                inputTypeHint="number"
                label="Per-return fee"
                min={0}
                placeholder="0.00"
                setValue={processingCost.setValue}
                state={disabled ? "disabled" : "default"}
                step={0.01}
                value={processingCost.value}
              />
            </Flex>
            <Flex pb="sm">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                or
              </Text>
            </Flex>
            <Flex dir="column" gap="xs" grow="1">
              <CoverageShareLabel />
              <RedoTextInput
                IconTrailing={() => <span>USD</span>}
                inputTypeHint="number"
                min={0}
                placeholder="0.00"
                setValue={processingCoverageShare.setValue}
                state={
                  disabled
                    ? "disabled"
                    : exceedsCoveragePrice
                      ? "error"
                      : "default"
                }
                step={0.01}
                value={processingCoverageShare.value}
              />
            </Flex>
          </Flex>
        )}
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setConsolidationOpen((v) => !v)}
          p="xl"
          style={CLICKABLE}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Consolidation
          </Text>
          <ChevronRightIcon
            style={consolidationOpen ? CHEVRON_OPEN : CHEVRON_CLOSED}
          />
        </Flex>
        {consolidationOpen && (
          <Flex dir="column" gap="sm" pb="xl" pl="xl" pr="xl">
            <CoverageShareLabel />
            <RedoTextInput
              IconTrailing={() => <span>USD</span>}
              inputTypeHint="number"
              min={0}
              placeholder="0.00"
              setValue={consolidationCoverageShare.setValue}
              state={
                disabled
                  ? "disabled"
                  : exceedsCoveragePrice
                    ? "error"
                    : "default"
              }
              step={0.01}
              value={consolidationCoverageShare.value}
            />
            <Text fontSize="xs" textColor="tertiary">
              If empty, shipping costs are passed through to the merchant.
            </Text>
          </Flex>
        )}
      </div>
    </SettingCard>
  );
});
